import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Slide,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import OptimicitiLogo from "assets/img/brand/logo_teal_navy-cropped.png";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const baseDialogPaperSx = {
  borderRadius: 3,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 20px 60px rgba(23, 43, 77, 0.18)",
  overflow: "hidden",
};

const baseDialogActionsSx = {
  p: 2,
  gap: 1,
};


const BaseDataEntryDialog = ({
  open,
  title,
  description,
  maxWidth,
  children,
  actions,
  onClose,
  titleId,
  descriptionId,
  showLogo = true,
  fullScreen = false,
  contentSx = {},
  actionsSx = {},
  PaperProps = {},
  logoSize = 28,
  ...other
}) => {
  const resolvedTitleId = titleId || "data-entry-dialog-title";
  const resolvedDescriptionId = descriptionId || "data-entry-dialog-description";

  const handleDialogClick = (e) => {
    e.stopPropagation();
    if (other.onClick) {
      other.onClick(e);
    }
  };

  return (
    <Dialog
      open={Boolean(open)}
      TransitionComponent={Transition}
      onClose={onClose}
      aria-labelledby={resolvedTitleId}
      aria-describedby={description ? resolvedDescriptionId : undefined}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        ...PaperProps,
        sx: {
          ...baseDialogPaperSx,
          ...PaperProps.sx,
        },
      }}
      {...other}
      onClick={handleDialogClick}
    >
      <DialogTitle
        id={resolvedTitleId}
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          background:
            "linear-gradient(180deg, rgba(230, 252, 251, 0.55) 0%, rgba(255, 255, 255, 1) 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          {showLogo && (
            <Box
              component="img"
              src={OptimicitiLogo}
              alt="Optimiciti Logo"
              sx={{
                height: logoSize,
                width: logoSize,
                flexShrink: 0,
                borderRadius: "8px",
              }}
            />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: "dark.main",
                lineHeight: 1.2,
                fontSize: "1.05rem",
                mb: description ? 0.25 : 0,
              }}
            >
              {title}
            </Typography>

            {description ? (
              <Typography
                id={resolvedDescriptionId}
                variant="body2"
                sx={{ color: "text.secondary", lineHeight: 1.2, fontSize: "0.85rem" }}
              >
                {description}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "rgba(0, 146, 139, 0.08)",
              color: "primary.main",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2.5, ...contentSx }}>{children}</DialogContent>

      {actions ? (
        <>
          <Divider />
          <DialogActions sx={{ ...baseDialogActionsSx, ...actionsSx }}>{actions}</DialogActions>
        </>
      ) : null}
    </Dialog>
  );
};

BaseDataEntryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", false]),
  children: PropTypes.node.isRequired,
  actions: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  titleId: PropTypes.string,
  descriptionId: PropTypes.string,
  showLogo: PropTypes.bool,
  fullScreen: PropTypes.bool,
  contentSx: PropTypes.object,
  actionsSx: PropTypes.object,
  PaperProps: PropTypes.object,
};

export default BaseDataEntryDialog;
